import useStore, { ITeam } from 'state/knovStore'
import { uniqBy } from 'lodash'
import {
    getCenterPanel,
    getRightPanel,
    getSwiperRef,
    slideTo,
    SWIPE_SPEED_FAST,
    SWIPE_SPEED_SLOW,
} from 'state/imperativeApis/swiperApi'
import { centerOpenPlacementIntent, openCenterCore } from 'refactor/panels/openCenterCore'
import { primeQuestForInstantOpen } from 'refactor/panels/primeChildQuestForOpen'
import questModel from 'components/quests/questModel'
import { getCachedQuest, getCachedUser } from 'state/cache'
import { isMobile } from 'lib/device'
import { getQuestsQueryKey, serializeFilterKey } from 'refactor/hooks/api/useGetQuests'
import queryClient from 'api/queryClient'
import { useMemo } from 'react'
import api from 'api/api'
import {
    buildNotificationsFilter,
    clearStream,
    questFilterFromPath,
    questPathFromFilter,
} from 'components/filters/streamFilterUtils'
import { removeInactiveNotificationQuestQueries } from 'refactor/hooks/api/notificationQuestQueries'
import { canonicalizeQuestFilter } from 'lib/questFilterCanonicalization'
import { refreshFilterForNavigation } from 'components/filters/filterNavigationRefresh'
import { defaultPrivateStreamFilter, myStreamsEnabled } from 'lib/myStreamsFeature'
import { memeHubPath } from 'lib/seoPaths'
// uuid no longer needed here

export type Filter = {
    space_id?: string
    questId?: string
    questIds?: string[]
    notifications?: boolean
    message_notis?: boolean
    upvalue_notis?: boolean
    system_notis?: boolean
    private?: boolean
    // Private All tab: union of threads authored by or mentioning the viewer.
    private_all?: boolean
    clip?: boolean
    public_clip?: boolean
    public?: boolean
    following?: boolean
    ordinals?: boolean
    // The Market surface. With `ordinals` it is the Items lens (ordinals with
    // an open ask, the inventory grid); with `market_cards` it is the Activity
    // lens (the system listing / offer / sale cards).
    market?: boolean
    // Activity lens of the Market surface: every system market card. The
    // server treats it as the public scope narrowed to the cards.
    market_cards?: boolean
    // Ordinals stream tabs: absent = the public wall, 'following' = ordinals
    // owned by people the viewer follows.
    ordinals_scope?: 'following' | 'mine'
    // Ordinals public-wall quality dial: minimum upvalue (sats). Only the
    // whitelisted stops in ordinalsThreshold.ts are meaningful; absent falls
    // back to the viewer's saved stop, then the product default.
    min_sats?: number
    hodlocker?: boolean
    twetch?: boolean
    treechat?: boolean
    link?: boolean
    all?: boolean
    user?: string
    // Profile tabs split authored root threads from authored non-root branches.
    profile_thread_scope?: 'posts' | 'replies'
    users?: string[]
    // Profile-specific ordinals stream: treat as { user: <id>, ordinals: true }
    user_ordinals?: string
    // The profile's Music tab: the user's radio songs (see MusicArtistProfile).
    user_music?: string
    from_user?: string
    public_user?: string
    team_ids?: string[]
    history?: boolean
    starred?: boolean
    meme?: string
    order?: OrderType<Filter> // some orders (locked and unlocked only apply for public streams)
    // Time window for the Top orders (upvote/upvalue): Top 1h / 24h / week / ….
    // Absent = all time. Ignored by every other order.
    top_window?: '1h' | '24h' | 'week' | 'month' | 'year'
    query?: string
    answerId?: string
    participants?: string
    streams?: boolean
    // Which container's streams the streams list shows: the public directory
    // (default), streams you host ('mine'), your memberships ('following'), or
    // your private memberships ('private').
    streams_scope?: 'mine' | 'following' | 'private'
    memes?: boolean
    memes_scope?: 'mine'
    // Authenticated-only Private tab: direct mentions of the viewer, regardless
    // of who authored the thread.
    private_mentions?: boolean
    // Private Messages tab: the viewer's direct-message conversations.
    private_messages?: boolean
    // Private Requests tab: message requests waiting on the viewer.
    private_message_requests?: boolean
    search?: boolean
    depth?: 'roots' | 'branches' | 'all'
    archived?: boolean
}

type NewPanelContext = {
    newThread?: boolean
    draftFilter?: Filter
}

type SharedOrderTypes =
    | 'vote'
    | 'lockvalue'
    | 'relev'
    | 'semantic'
    | 'time'
    | 'hot'
    | 'new'
    | 'random'
    | 'top_new'
    // Value orders of the public, ordinal and meme streams.
    | 'upvalue'
    | 'upvalueTrending'
    // Market tab: listings ranked by asking price, cheapest first.
    | 'price_asc'
type OrderType<T> = T extends { public: true }
    ? SharedOrderTypes | 'locked' | 'unlocked'
    : SharedOrderTypes

const STREAM_FILTER_KEYS: Array<keyof Filter> = [
    'notifications',
    'message_notis',
    'upvalue_notis',
    'system_notis',
    'private',
    'clip',
    'public_clip',
    'public',
    'following',
    'ordinals',
    'market',
    'hodlocker',
    'twetch',
    'treechat',
    'participants',
    'link',
    'all',
    'user',
    'public_user',
    'from_user',
    'team_ids',
    'history',
    'starred',
    'streams',
    'memes',
    'meme',
    'search',
]

function isStreamFilter(filter?: Filter | null) {
    if (!filter) return false
    if (filter.questId || filter.questIds || filter.answerId) return false

    return STREAM_FILTER_KEYS.some(key => {
        const value = filter[key]
        if (Array.isArray(value)) {
            return value.length > 0
        }
        return Boolean(value)
    })
}

const arraysEqual = (a: unknown, b: unknown) => {
    if (!Array.isArray(a) || !Array.isArray(b)) return false
    if (a.length !== b.length) return false
    return a.every((_value, index) => {
        const left = a[index]
        const right = b[index]
        if (left === right) return true
        if (left == null || right == null) return left === right
        if (typeof left === 'object' || typeof right === 'object') {
            try {
                return JSON.stringify(left) === JSON.stringify(right)
            } catch {
                return false
            }
        }
        return String(left) === String(right)
    })
}

const valuesEqual = (currentValue: unknown, nextValue: unknown) => {
    if (Array.isArray(nextValue)) {
        return arraysEqual(currentValue, nextValue)
    }

    if (typeof nextValue === 'object' && nextValue !== null) {
        try {
            return JSON.stringify(currentValue) === JSON.stringify(nextValue)
        } catch {
            return false
        }
    }

    if (typeof nextValue === 'number' && typeof currentValue === 'string') {
        return String(nextValue) === currentValue
    }

    if (typeof nextValue === 'string' && typeof currentValue === 'number') {
        return nextValue === String(currentValue)
    }

    return currentValue === nextValue
}

const filtersAreEquivalent = (currentFilter: Filter | undefined, nextFilter: Partial<Filter>) => {
    if (!currentFilter) return false
    // Market Items shares the ordinal grid but is a distinct navigation target.
    if (Boolean(currentFilter.market) !== Boolean(nextFilter.market)) return false
    if (currentFilter.search && !nextFilter.search) return false
    return Object.keys(nextFilter).every(key => {
        const nextValue = nextFilter[key]
        if (typeof nextValue === 'undefined') return true
        const currentValue = currentFilter[key]
        return valuesEqual(currentValue, nextValue)
    })
}

const normalizeComparableFilter = (filter?: Filter | null) => {
    if (!filter || !isStreamFilter(filter)) return filter
    return canonicalizeQuestFilter(withDepth(filter))
}

export function useAvailableTeams(user) {
    const publicTeams = useStore(state => state.publicTeams) || []
    const userStreams = useStore(state => state.userStreams) || []

    return useMemo(() => {
        let teams = uniqBy([...userStreams, ...publicTeams], 'id')
        if (user?.space_teams) {
            // Prefer live store data over the bootstrapped gon snapshot so stream
            // settings updates show up immediately without a full page reload.
            teams = uniqBy([...teams, ...user.space_teams], 'id')
        }
        return teams
    }, [user?.space_teams, userStreams, publicTeams])
}

export function getDepth(filter: Filter): string {
    const depthKey = getStreamId(filter)
    return filter?.depth || (gon.currentUser?.space_options?.[depthKey] as string) || 'roots'
}

export function withDepth(filter: Filter): Filter {
    const depth = getDepth(filter) as 'roots' | 'branches' | 'all'
    return { ...filter, depth }
}

export function filterFromPath(path: string, query?: string): Filter {
    const filter: Filter = {}

    // Handle search paths
    if (path.endsWith('/search')) {
        filter.search = true
        filter.query = query || ''

        // Extract the stream context from the path
        const streamMatch = path.match(/\/stream\/([^\/]+)\/search/)
        if (streamMatch) {
            const streamId = streamMatch[1]

            // Handle special stream types
            switch (streamId) {
                case 'all':
                    filter.all = true
                    break
                case 'private':
                    filter.private = true
                    break
                case 'public':
                    filter.public = true
                    break
                case 'notifications':
                    filter.notifications = true
                    break
                case 'starred':
                    filter.starred = true
                    break
                case 'history':
                    filter.history = true
                    break
                case 'ordinals':
                    filter.ordinals = true
                    break
                case 'clips':
                    filter.clip = true
                    break
                case 'public-clips':
                    filter.public_clip = true
                    break
                case 'link-access':
                case 'shared':
                    filter.link = true
                    break
                case 'hodlocker':
                    filter.hodlocker = true
                    break
                case 'twetch':
                    filter.twetch = true
                    break
                case 'treechat':
                    filter.treechat = true
                    break
                default:
                    // It's a team/stream ID
                    filter.team_ids = [streamId]
            }
        }
    } else {
        // Handle non-search paths
        const questFilter = questFilterFromPath(path)
        if (questFilter) {
            Object.assign(filter, questFilter)
        } else if (path.startsWith('/user/')) {
            const userMatch = path.match(/^\/user\/([^/]+)(?:\/(ordinals|replies|music))?\/?$/)
            const userId = userMatch?.[1]
            const profileTab = userMatch?.[2]
            if (userId && profileTab === 'ordinals') {
                filter.user_ordinals = userId
            } else if (userId && profileTab === 'music') {
                filter.user_music = userId
            } else if (userId) {
                filter.user = userId
                filter.profile_thread_scope = profileTab === 'replies' ? 'replies' : 'posts'
            }
        } else if (path.startsWith('/people/')) {
            const participants = path.substring(8)
            filter.participants = participants
        } else if (path === '/stream/people') {
            // Legacy People stream URLs land on the new Home.
            filter.following = true
        } else if (path === '/streams/mine') {
            filter.streams = true
            filter.streams_scope = 'mine'
        } else if (path === '/streams/following') {
            filter.streams = true
            filter.streams_scope = 'following'
        } else if (path === '/streams/private') {
            filter.streams = true
            filter.streams_scope = 'private'
        } else if (path === '/streams' || path === '/discovery/streams') {
            filter.streams = true
        } else if (path === '/memes/mine') {
            filter.memes = true
            filter.memes_scope = 'mine'
        } else if (path === '/memes' || path === '/discovery/memes') {
            filter.memes = true
        } else {
            // Handle standard stream paths
            switch (path) {
                case '/stream/all':
                    filter.all = true
                    filter.order = 'time'
                    break
                case '/stream/private':
                    filter.private = true
                    if (myStreamsEnabled()) filter.private_all = true
                    filter.order = 'time'
                    break
                case '/stream/private/threads':
                    filter.private = true
                    filter.order = 'time'
                    break
                case '/stream/private/mentions':
                    filter.private = true
                    filter.private_mentions = true
                    filter.order = 'time'
                    break
                case '/stream/private/messages':
                    filter.private = true
                    filter.private_messages = true
                    filter.order = 'time'
                    break
                case '/stream/private/requests':
                    filter.private = true
                    filter.private_message_requests = true
                    filter.order = 'time'
                    break
                case '/stream/public':
                    filter.public = true
                    break
                case '/stream/following':
                    filter.following = true
                    break
                case '/stream/notifications':
                    filter.notifications = true
                    break
                case '/stream/starred':
                    filter.starred = true
                    filter.order = 'time'
                    break
                case '/stream/history':
                    filter.history = true
                    filter.order = 'time'
                    break
                case '/market':
                case '/market/activity':
                    // Activity is the default lens; the explicit URL stays valid.
                    filter.market = true
                    filter.market_cards = true
                    break
                case '/market/items':
                    filter.market = true
                    filter.ordinals = true
                    break
                case '/stream/ordinals':
                    filter.ordinals = true
                    break
                case '/stream/ordinals/market':
                    // Legacy Ordinals > Market tab: now the Market surface's
                    // Items lens, same filter.
                    filter.market = true
                    filter.ordinals = true
                    break
                case '/stream/ordinals/mine':
                    filter.ordinals = true
                    filter.ordinals_scope = 'mine'
                    break
                case '/stream/ordinals/following':
                    // Following is temporarily hidden; keep old links on the
                    // global Ordinals stream instead of restoring that scope.
                    filter.ordinals = true
                    break
                case '/stream/clips':
                    filter.clip = true
                    filter.order = 'time'
                    break
                case '/stream/public-clips':
                    filter.public_clip = true
                    filter.order = 'time'
                    break
                case '/stream/link-access':
                case '/stream/shared':
                    filter.link = true
                    filter.order = 'time'
                    break
                case '/stream/hodlocker':
                    filter.hodlocker = true
                    break
                case '/stream/twetch':
                    filter.twetch = true
                    break
                case '/stream/treechat':
                    filter.treechat = true
                    break
                default:
                    // Check if it's a stream ID path
                    const streamMatch = path.match(/^\/stream\/([^\/]+)$/)
                    if (streamMatch) {
                        filter.team_ids = [streamMatch[1]]
                    }
            }
        }
    }

    return filter
}

export function pathFromFilter(filter) {
    let team = null
    if (gon.currentUser && filter.team_ids && filter.team_ids[0]) {
        const state = useStore.getState()
        const publicTeams = state.publicTeams || []
        const userSpaceTeams = gon.currentUser?.space_teams || []
        // Use the same logic as useAvailableTeams - combine user space teams and public teams
        const allTeams = uniqBy([...userSpaceTeams, ...publicTeams], 'id')
        team = allTeams.find(t => t.id === filter.team_ids[0])
    }

    const userId = filter.user_ordinals || filter.user_music || filter.user
    // The space member list is lazy. Navigation must work before it loads (and
    // for profiles outside the current space), without waiting for that request.
    const user = userId
        ? getCachedUser(userId) ||
          useStore.getState().users?.find(u => String(u.id) === String(userId))
        : null
    const userPath = userId ? user?.path || `/user/${encodeURIComponent(userId)}` : null

    let path
    if (filter.questId) {
        path = getCachedQuest(filter.questId)?.is_draft ? '/new' : questPathFromFilter(filter)
    } else if (filter.meme) {
        // Meme hub filters also carry `all: true`; keep this before the generic
        // all-stream case so panel navigation retains the canonical hub URL.
        path = memeHubPath(filter.meme) || ''
    } else if (team) {
        path = team.path
    } else if (filter.team_ids && filter.team_ids[0]) {
        // Fallback for hidden or not-yet-loaded teams
        path = `/stream/${filter.team_ids[0]}`
    } else if (userPath && filter.user_ordinals) {
        path = `${userPath.replace(/\/$/, '')}/ordinals`
    } else if (userPath && filter.user_music) {
        path = `${userPath.replace(/\/$/, '')}/music`
    } else if (userPath) {
        path =
            filter.profile_thread_scope === 'replies'
                ? `${userPath.replace(/\/$/, '')}/replies`
                : userPath
    } else if (filter.private && filter.private_message_requests) {
        path = '/stream/private/requests'
    } else if (filter.private && filter.private_messages) {
        path = '/stream/private/messages'
    } else if (filter.private && filter.private_mentions) {
        path = '/stream/private/mentions'
    } else if (filter.private && filter.private_all) {
        path = '/stream/private'
    } else if (filter.private) {
        path = myStreamsEnabled() ? '/stream/private/threads' : '/stream/private'
    } else if (filter.following) {
        path = '/stream/following'
    } else if (filter.clip) {
        path = '/stream/clips'
    } else if (filter.public_clip) {
        path = '/stream/public-clips'
    } else if (filter.search) {
        // For search, we need to determine which stream context we're in
        if (filter.team_ids && filter.team_ids[0]) {
            path = `/stream/${filter.team_ids[0]}/search`
        } else if (team) {
            path = `/stream/${team.id}/search`
        } else {
            // Default to all stream search
            path = '/stream/all/search'
        }
    } else if (filter.all) {
        path = '/stream/all'
    } else if (filter.hodlocker) {
        path = '/stream/hodlocker'
    } else if (filter.twetch) {
        path = '/stream/twetch'
    } else if (filter.treechat) {
        path = '/stream/treechat'
    } else if (filter.public) {
        path = '/stream/public'
    } else if (filter.streams) {
        path =
            filter.streams_scope === 'mine'
                ? '/streams/mine'
                : filter.streams_scope === 'following'
                ? '/streams/following'
                : filter.streams_scope === 'private'
                ? '/streams/private'
                : '/streams'
    } else if (filter.memes) {
        path = filter.memes_scope === 'mine' ? '/memes/mine' : '/memes'
    } else if (filter.participants) {
        path = '/people/' + filter.participants
    } else if (filter.notifications) {
        path = '/stream/notifications'
    } else if (filter.link) {
        path = '/stream/link-access'
    } else if (filter.history) {
        path = '/stream/history'
    } else if (filter.starred) {
        path = '/stream/starred'
    } else if (filter.market) {
        path = filter.market_cards ? '/market' : '/market/items'
    } else if (filter.ordinals) {
        path = filter.ordinals_scope === 'mine' ? '/stream/ordinals/mine' : '/stream/ordinals'
    } else {
        path = ''
    }
    return path
}

export function ensureEmptyEnds() {
    const panels = useStore.getState().panels
    const firstPanel = panels.state[0]
    const lastPanel = panels.state[panels.state.length - 1]
    if (!firstPanel?.empty) {
        panels.prependEmptyPanel()
    } else if (!lastPanel?.empty) {
        panels.appendEmptyPanel()
    }
}

export function insertCenter(newFilter, highlightAnswer = false) {
    const panels = useStore.getState().panels
    const numPanels = useStore.getState().numPanels
    const centerPanel = getCenterPanel()

    // Check if the center panel already has this filter
    const centerFilter = normalizeComparableFilter(centerPanel?.filter)
    const comparableNewFilter = normalizeComparableFilter(newFilter)
    const filtersMatch = filtersAreEquivalent(centerFilter, comparableNewFilter)

    if (filtersMatch) {
        if (isStreamFilter(newFilter) && centerPanel && centerPanel.triggerStreamRefresh) {
            const triggered = centerPanel.triggerStreamRefresh({ smoothScroll: false })
            if (!triggered) {
                centerPanel.shake?.()
            }
        } else {
            centerPanel?.shake?.()
        }
        return
    }

    if (isMobile || numPanels === 1) {
        const panelId = centerPanel?.panelId
        panels.insertPanelRight(
            panelId,
            { filter: newFilter, empty: false, deferContentUntilTransition: true },
            { animate: false },
        )
        ensureEmptyEnds()

        // In mobile/one-panel mode, slide to the newly inserted panel
        requestAnimationFrame(() => {
            const swiper = getSwiperRef()?.current
            if (swiper) {
                // Get the index of the panel we just inserted (it's to the right of current)
                const currentIndex = panels.getIndexById(panelId)
                const targetIndex = currentIndex + 1

                // Verify the panel exists before sliding
                const panelState = useStore.getState().panels.state
                if (panelState[targetIndex] && !panelState[targetIndex].empty) {
                    if (numPanels === 1 && !isMobile) {
                        slideTo(targetIndex, SWIPE_SPEED_SLOW)
                    } else {
                        swiper.slideTo(targetIndex, SWIPE_SPEED_FAST)
                    }
                }
            }
        })
    } else {
        const panelId = getRightPanel()?.panelId
        panels.insertPanelLeft(panelId, { filter: newFilter, empty: false }, { animate: true })
    }

    if (newFilter.answerId) {
        //TODO
    }
}

// Explicit center insertion with slide + optional highlight behavior.
// Prefer this over insertCenter for deterministic UX and to avoid reliance on useSlideEffects.
export function openCenter(newFilter: Filter, panelContext: NewPanelContext = {}) {
    const panels = useStore.getState().panels
    const numPanels = useStore.getState().numPanels
    const swiper = getSwiperRef()?.current
    // Overlap the quest fetch with the panel insert/slide so the target's row can exist by
    // first commit (no-op unless instant_reply_nav is on and the cache can't produce it).
    if (newFilter?.questId && newFilter?.answerId) {
        primeQuestForInstantOpen(newFilter.questId, newFilter.answerId)
    }

    // If center already matches (ignoring order), shake or highlight answer
    const centerPanel = getCenterPanel()
    const centerFilter = normalizeComparableFilter(centerPanel?.filter)
    const comparableNewFilter = normalizeComparableFilter(newFilter)
    const filtersMatch = filtersAreEquivalent(centerFilter, comparableNewFilter)
    if (filtersMatch) {
        if (centerPanel && Object.keys(panelContext).length) {
            useStore.getState().panels.editPanel(centerPanel.panelId, panelContext)
        }
        if (newFilter.answerId) {
            void centerPanel?.requestAnswerReveal?.(newFilter.answerId, {
                questId: newFilter.questId,
                shake: true,
                highlight: true,
            })
        } else {
            if (isStreamFilter(newFilter) && centerPanel && centerPanel.triggerStreamRefresh) {
                const triggered = centerPanel.triggerStreamRefresh({ smoothScroll: false })
                if (!triggered) {
                    centerPanel.shake?.()
                }
            } else {
                centerPanel?.shake?.()
            }
        }
        return
    }

    if (isStreamFilter(newFilter)) {
        void refreshFilterForNavigation(withDepth(newFilter))
    }

    // In desktop 3-panel mode, we orchestrate without Swiper slide.

    if (isMobile || numPanels === 1) {
        // Anchor on currently active
        const activeIndex = swiper?.activeIndex ?? 0
        const currentPanels = useStore.getState().panels.state
        const anchorPanelId = currentPanels[activeIndex]?.panelId || centerPanel?.panelId
        const placementIntent = centerOpenPlacementIntent(newFilter)

        panels.insertPanelRight(
            anchorPanelId,
            {
                ...panelContext,
                filter: newFilter,
                empty: false,
                deferContentUntilTransition: true,
                ...(placementIntent ? { pendingOpenPlacement: placementIntent } : {}),
            },
            { animate: false, actionParams: { manualSlide: true } },
        )

        // Slide to inserted panel (right of anchor) and highlight
        requestAnimationFrame(() => {
            const currentIndex = panels.getIndexById(anchorPanelId)
            const targetIndex = currentIndex + 1
            if (targetIndex > -1 && swiper) {
                slideTo(
                    targetIndex,
                    numPanels === 1 && !isMobile ? SWIPE_SPEED_SLOW : SWIPE_SPEED_FAST,
                )
                if (newFilter.answerId) {
                    const fn = () => {
                        const p = useStore.getState().panels.state[swiper.activeIndex]
                        void p?.ensureAnswerReveal?.(newFilter.answerId, {
                            questId: newFilter.questId,
                            shake: true,
                            highlight: true,
                        })
                        swiper.off && swiper.off('slideChangeTransitionEnd', fn)
                    }
                    swiper.once
                        ? swiper.once('slideChangeTransitionEnd', fn)
                        : swiper.on?.('slideChangeTransitionEnd', fn)
                }
            }
        })
    } else {
        // 3-panel desktop: orchestrate open-center without sliding
        openCenterCore({ filter: newFilter, meta: panelContext, ensureUnique: true })
    }
}

export function openNotificationsStream(notificationFilter: Partial<Filter> = {}) {
    const filter = buildNotificationsFilter(getCenterPanel()?.filter, notificationFilter)
    openCenter(withDepth(filter))
}

export default function useStreamFilter(
    panelId = getCenterPanel()?.panelId,
    opts: { append?: boolean; insertAndCenter?: boolean } = { append: true },
) {
    // Default to center panel.
    const getFilter = () => useStore.getState().panels.getPanel(panelId)?.filter
    let filter = getFilter()
    if (filter?.questId) {
        const quest = getCachedQuest(filter.questId)
        //console.log('USE STREAM FILTER', quest)
        if (quest) {
            const perms = questModel.getFilter(quest)
            //console.log('USE STREAM FILTER PERMS', perms)
            filter = { ...clearStream(filter), ...perms }
        }
    }
    const editPanel = useStore.getState().panels.editPanel
    const appendSession = useStore.getState().panels.appendSession
    const refreshCurrentPanel = () => {
        const targetPanel = useStore.getState().panels.getPanel(panelId)
        return targetPanel?.triggerStreamRefresh?.({ smoothScroll: false }) ?? false
    }

    const selectNewFilter = (newFilter = {}, panelContext: NewPanelContext = {}) => {
        const newFilterWithDepth = withDepth(newFilter)
        const nextPanelContext = {
            newThread: false,
            draftFilter: undefined,
            ...panelContext,
        }
        if (opts.append) {
            // Insert-and-center explicitly to avoid hidden auto-slides
            openCenter(newFilterWithDepth, nextPanelContext)
        } else if (opts.insertAndCenter) {
            openCenter(newFilterWithDepth, nextPanelContext)
        } else {
            const filtersMatch = filtersAreEquivalent(
                normalizeComparableFilter(filter),
                normalizeComparableFilter(newFilterWithDepth),
            )
            if (!filtersMatch && isStreamFilter(newFilterWithDepth)) {
                void refreshFilterForNavigation(newFilterWithDepth)
            }
            // Directly set filter on target panel without sliding
            const centerBefore = getCenterPanel()
            const centerBeforeIdx = centerBefore
                ? useStore.getState().panels.getIndexById(centerBefore.panelId)
                : -1
            editPanel(panelId, {
                ...nextPanelContext,
                filter: newFilterWithDepth,
                empty: false,
            })

            // If this selection occurred in the left empty panel in 3-panel mode, restore left buffer
            const numPanels = useStore.getState().numPanels
            if (!isMobile && numPanels !== 1) {
                const panelsApi = useStore.getState().panels
                const center = getCenterPanel()
                const anchor = panelsApi.getPanel(panelId)
                if (center && anchor) {
                    const idxCenter = panelsApi.getIndexById(center.panelId)
                    const idxAnchor = panelsApi.getIndexById(panelId)
                    const isLeftOfCenter = idxAnchor === idxCenter - 1
                    if (isLeftOfCenter) {
                        // Add an offscreen empty panel to the left so we keep the [empty, active, empty] structure
                        panelsApi.prependEmptyPanel()
                        // Keep the visual window stable by shifting center one step right (instant)
                        // Defer slide adjust to after DOM/Virtual slides update
                        const swiper = getSwiperRef()?.current
                        if (swiper) {
                            requestAnimationFrame(() => {
                                swiper.slideTo(swiper.activeIndex + 1, 0)
                            })
                        }
                    }
                }
            }

            ensureEmptyEnds()

            if (filtersMatch) {
                refreshCurrentPanel()
            }
        }
    }

    const selectHistory = (historyFilter = {}) => {
        const newFilter = { ...clearStream(filter), ...historyFilter, history: true }
        selectNewFilter(newFilter)
    }

    const selectStarred = (starredFilter = {}) => {
        const newFilter = { ...clearStream(filter), ...starredFilter, starred: true }
        selectNewFilter(newFilter)
    }

    const selectNotifications = (notiFilter = {}) => {
        const newFilter = buildNotificationsFilter(filter, notiFilter)
        if (useStore.getState().numNoti > 0) {
            removeInactiveNotificationQuestQueries()
        }
        selectNewFilter(newFilter)
    }

    const selectPrivate = (privateFilter = {}) => {
        console.log('SELECT PRIVATE', panelId, getFilter())
        const newFilter = {
            ...clearStream(filter),
            ...defaultPrivateStreamFilter(),
            ...privateFilter,
            private: true,
        }
        selectNewFilter(newFilter)
    }

    const selectClips = (clipFilter = {}) => {
        const newFilter = { ...clearStream(filter), ...clipFilter, clip: true }
        selectNewFilter(newFilter)
    }

    const selectPublicClips = (publicClipFilter = {}) => {
        const newFilter = { ...clearStream(filter), ...publicClipFilter, public_clip: true }
        selectNewFilter(newFilter)
    }

    const selectPublic = (publicFilter = {}) => {
        const newFilter = { ...clearStream(filter), ...publicFilter, public: true }
        selectNewFilter(newFilter)
    }

    const selectFollowing = (followingFilter = {}) => {
        const newFilter = { ...clearStream(filter), ...followingFilter, following: true }
        selectNewFilter(newFilter)
    }

    const selectTreechat = (treechatFilter = {}) => {
        const newFilter = { ...clearStream(filter), ...treechatFilter, treechat: true }
        selectNewFilter(newFilter)
    }

    const selectHodlocker = (hodlockerFilter = {}) => {
        const newFilter = { ...clearStream(filter), ...hodlockerFilter, hodlocker: true }
        selectNewFilter(newFilter)
    }

    const selectTwetch = (twetchFilter = {}) => {
        const newFilter = { ...clearStream(filter), ...twetchFilter, twetch: true }
        selectNewFilter(newFilter)
    }

    const selectOrdinals = (ordinalsFilter = {}) => {
        const newFilter = { ...clearStream(filter), ...ordinalsFilter, ordinals: true }
        selectNewFilter(newFilter)
    }

    // The Market surface, Activity lens unless the caller asks for Items
    // (`ordinals: true`).
    const selectMarket = (marketFilter: Filter = {}) => {
        const lens = marketFilter.ordinals ? {} : { market_cards: true }
        const newFilter = { ...clearStream(filter), ...lens, ...marketFilter, market: true }
        selectNewFilter(newFilter)
    }

    const selectStreams = (streamsFilter = {}) => {
        const newFilter = { ...clearStream(filter), ...streamsFilter, streams: true }
        selectNewFilter(newFilter)
    }

    const selectMemes = (memesFilter = {}) => {
        const newFilter = { ...clearStream(filter), ...memesFilter, memes: true }
        selectNewFilter(newFilter)
    }

    const selectSearch = (searchFilter = {}) => {
        const newFilter = { ...clearStream(filter), ...searchFilter, search: true }
        selectNewFilter(newFilter)
    }

    const selectLinks = (linkFilter = {}) => {
        const newFilter = { ...clearStream(filter), ...linkFilter, link: true }
        selectNewFilter(newFilter)
    }

    const selectAll = (allFilter = {}) => {
        const newFilter = { ...clearStream(filter), ...allFilter, all: true }
        selectNewFilter(newFilter)
    }

    const selectUser = (userId, userFilter = {}) => {
        const newFilter = { ...clearStream(filter), ...userFilter, user: userId }
        selectNewFilter(newFilter)
    }

    const selectParticipants = (participantsKey, participantsFilter = {}) => {
        const newFilter = {
            ...clearStream(filter),
            ...participantsFilter,
            participants: participantsKey,
        }
        selectNewFilter(newFilter)
    }

    const selectTeam = (teamId, teamFilter = {}) => {
        const newFilter = {
            ...clearStream(filter),
            ...teamFilter,
            ...(teamId !== undefined && teamId !== null ? { team_ids: [teamId] } : {}),
        }
        selectNewFilter(newFilter)
    }
    const teams = useAvailableTeams(gon?.currentUser)
    const filterTeamId = filter && filter.team_ids && filter.team_ids[0]

    const isNotiSelected = () => filter?.notifications
    const isPrivateSelected = () => filter?.private
    const isClipSelected = () => filter?.clip
    const isPublicClipSelected = () => filter?.public_clip
    const isPublicSelected = () => filter?.public
    const isFollowingSelected = () => filter?.following
    const isTreechatSelected = () => filter?.treechat
    const isHodlockerSelected = () => filter?.hodlocker
    const isTwetchSelected = () => filter?.twetch
    const isOrdinalsSelected = () => filter?.ordinals
    const isMarketSelected = () => filter?.market
    const isLinkSelected = () => filter?.link
    const isAllSelected = () => filter?.all
    const isUserSelected = () => filter?.user_ordinals || filter?.user_music || filter?.user
    const isTeamSelected = teamId => {
        if (!filter?.team_ids?.length) return false
        const normalizedTeamId =
            teamId === null || typeof teamId === 'undefined' ? null : String(teamId)
        if (!normalizedTeamId) return false
        return filter.team_ids.some(id => String(id) === normalizedTeamId)
    }
    const isParticipantsSelected = (participantsKey: string) =>
        filter?.participants && filter?.participants === participantsKey
    const isHistorySelected = () => filter?.history
    const isStarredSelected = () => filter?.starred
    const isStreamsSelected = () => filter?.streams
    const isMemesSelected = () => filter?.memes
    const isSearchSelected = () => filter?.search

    const setStreamOrder = order => {
        const newFilter = withDepth({ ...filter, order })
        editPanel(panelId, { filter: newFilter })
    }

    const setStreamFilter = newFilter => {
        const newFilterWithDepth = withDepth(newFilter)
        const filtersMatch = filtersAreEquivalent(
            normalizeComparableFilter(filter),
            normalizeComparableFilter(newFilterWithDepth),
        )
        editPanel(panelId, { filter: newFilterWithDepth })
        if (filtersMatch) {
            refreshCurrentPanel()
        }
    }

    const setQuery = (query, order) => {
        const nextFilter = { ...filter }
        const hasStreamOperator = typeof query === 'string' && /\bstream\s*:/i.test(query)
        const hasFromOperator = typeof query === 'string' && /\bfrom\s*:/i.test(query)
        const hasVisibilityOperator =
            typeof query === 'string' && /\bis\s*:\s*(public|private|clip)\b/i.test(query)

        if (hasStreamOperator) {
            delete nextFilter.team_ids
            delete nextFilter.public
            delete nextFilter.private
            delete nextFilter.clip
            delete nextFilter.public_clip
            delete nextFilter.ordinals
            delete nextFilter.user_ordinals
            delete nextFilter.following
            delete nextFilter.all
            delete nextFilter.hodlocker
            delete nextFilter.twetch
            delete nextFilter.treechat
            delete nextFilter.link
        }

        if (hasVisibilityOperator) {
            delete nextFilter.public
            delete nextFilter.private
            delete nextFilter.clip
            delete nextFilter.public_clip
        }

        if (hasFromOperator) {
            delete nextFilter.user
            delete nextFilter.users
            delete nextFilter.user_ordinals
            delete nextFilter.user_music
            delete nextFilter.from_user
            delete nextFilter.public_user
            delete nextFilter.participants
        }

        editPanel(panelId, {
            filter: { ...nextFilter, query, ...(order && { order }) },
        })
    }

    const setStreamDepth = depth => {
        const newFilter = { ...filter, depth }
        const queryKey = getQuestsQueryKey(newFilter)
        console.log('STREAM DEPTH: INVALIDATE', queryKey)
        queryClient.invalidateQueries({ queryKey, exact: true })
        editPanel(panelId, { filter: newFilter })
        const streamId = getStreamId(newFilter)
        api.updateUserSpaceOptions({ [streamId]: depth })
    }

    return {
        filter,
        selectNewFilter,
        selectNotifications,
        selectPrivate,
        selectClips,
        selectPublicClips,
        selectTreechat,
        selectHodlocker,
        selectTwetch,
        selectOrdinals,
        selectMarket,
        selectUser,
        appendSession,

        selectPublic,
        selectFollowing,
        selectLinks,
        selectAll,
        selectTeam,
        selectHistory,
        isHistorySelected,
        selectStarred,
        isStarredSelected,
        selectStreams,
        isStreamsSelected,
        selectMemes,
        isMemesSelected,
        selectSearch,
        isSearchSelected,
        selectParticipants,
        isParticipantsSelected,
        teams,
        filterTeamId,

        setStreamOrder,
        setStreamFilter,
        setQuery,
        setStreamDepth,

        isNotiSelected,
        isPrivateSelected,
        isPublicSelected,
        isFollowingSelected,
        isTreechatSelected,
        isHodlockerSelected,
        isTwetchSelected,
        isOrdinalsSelected,
        isMarketSelected,
        isLinkSelected,
        isClipSelected,
        isPublicClipSelected,
        isAllSelected,
        isUserSelected,
        isTeamSelected,
    }
}

// SHAME SHAME SHAME
export function getStreamId(filter) {
    const user = gon.currentUser

    let streamId

    if (filter && filter.user_ordinals) {
        streamId = `stream:user_ordinals:${filter.user_ordinals}`
    } else if (filter && filter.user) {
        streamId = `stream:user:${filter.user}`
    } else if (filter && filter.all) {
        streamId = 'stream:all'
    } else if (user && filter && filter.team_ids && filter.team_ids.length > 0) {
        streamId = `stream:team:${filter.team_ids[0]}`
    } else if (filter && filter.notifications) {
        streamId = 'stream:notifications'
    } else if (filter && filter.private) {
        streamId = 'stream:private'
    } else if (filter?.link) {
        streamId = 'stream:link'
    } else if (filter?.clip) {
        streamId = 'stream:clips'
    } else if (filter?.public_clip) {
        streamId = 'stream:public_clips'
    } else if (filter?.market) {
        streamId = filter.market_cards ? 'stream:market:activity' : 'stream:market'
    } else if (filter?.public) {
        streamId = 'stream:public'
    } else if (filter?.following) {
        streamId = 'stream:following'
    } else if (filter?.hodlocker) {
        streamId = 'stream:hodlocker'
    } else if (filter?.twetch) {
        streamId = 'stream:twetch'
    } else if (filter?.treechat) {
        streamId = 'stream:treechat'
    } else if (filter?.history) {
        streamId = 'stream:history'
    } else if (filter?.starred) {
        streamId = 'stream:starred'
    } else if (filter?.streams) {
        streamId = filter.streams_scope ? `streams:${filter.streams_scope}` : 'discovery:streams'
    } else if (filter?.memes) {
        streamId = 'discovery:memes'
    } else if (filter?.participants) {
        streamId = 'stream:participants'
    } else if (filter?.meme) {
        streamId = `stream:meme:${filter.meme}`
    }

    return streamId
}
